<template>
  <div class="workspaceAlertsContainer">
    <error-alert
      v-if="showDestroyFailedAlert"
      @click="$emit('retry:destroy')"
      @cancel="$emit('unlock:workspace')"
      :feedback="workspace.lastErrorTitle"
    />

    <deployed-with-error-alert
      v-else-if="errorTypeDeployedWithError"
      @redeploy="$emit('redeploy')"
      @destroy="$emit('destroy')"
      :feedback="workspace.lastErrorTitle"
      :disabled="loading"
    />

    <error-alert
      v-else-if="showErrorAlert || !disableErrorAlertActions"
      @click="$emit('unlock')"
      @cancel="$emit('unlock:workspace')"
      :disabled="disableErrorAlertActions"
      :feedback="workspace.lastErrorTitle"
    />

    <update-alert
      v-if="showPendingUpdate && !showErrorAlert && !showNoAppsAlert"
      @click="$emit('redeploy')"
      @discard="$emit('discard')"
    />

    <dns-messages
      v-if="showCertificate && !isRunning"
      @click="$emit('redeploy')"
      @renew:certificate="$emit('renew:certificate')"
      :certificate="certificate"
      :loading="loading"
    />

    <div v-if="showPendingServicesPendingConfiguration" class="alertErrorType">
      <generic-alert icon="action_remove_box" type="warning">
        <p>
          {{ t("servicePendingConfiguration.text") }}
        </p>
      </generic-alert>
    </div>

    <no-apps v-if="showNoAppsAlert" @discard="$emit('discard')" />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "@vue/composition-api"
import { CertificateVisibleState, WorkspaceTemplate, WorkspaceStatusEnum } from "@/types/workspace"
import { WorkspaceStates, Draft, DeployedWithError } from "@/config/workspace-states"
import { useWizard } from "@/composable/useWizard"
import { useStore } from "@/composable/useStore"
import { WizardConfigItem } from "@/types/wizard"
import { createTranslationModule } from "@/plugins/i18n"
import ErrorAlert from "./ErrorAlert.vue"
import UpdateAlert from "./UpdateAlert.vue"
import NoApps from "./NoApps.vue"
import DnsMessages from "./DnsMessages.vue"
import DeployedWithErrorAlert from "./DeployedWithErrorAlert.vue"
import Utils from "@/utils/utils"
import GenericAlert from "./GenericAlert.vue"

export default defineComponent({
  name: "WorkspaceAlerts",
  props: {
    workspace: {
      type: Object as PropType<WorkspaceTemplate>,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    }
  },
  setup(props) {
    const t = createTranslationModule("WorkspaceEdit.alerts")
    const { spaceModule, usersModule } = useStore()

    const showErrorAlert = computed(() => {
      return !!props.workspace.lastErrorTitle
    })

    const updateWarning = computed(() => {
      return !!props.workspace.updateWarning
    })

    const errorTypeDeployedWithError = computed(() => {
      return props.workspace.status === DeployedWithError
    })

    const disableErrorAlertActions = computed(() => {
      const { ArchiveFailed, DeployFailed, DeployedWithError } = WorkspaceStatusEnum
      const errorStates = [ArchiveFailed, DeployFailed, DeployedWithError]

      return !errorStates.includes(props.workspace.status)
    })

    const showDestroyFailedAlert = computed(() => {
      const { DestroyFailed } = WorkspaceStatusEnum
      return props.workspace.status === DestroyFailed
    })

    const showCertificate = computed(() => {
      const { warning, danger } = CertificateVisibleState

      return certificate.value && [warning, danger].includes(certificate.value.visibleState)
    })

    const certificate = computed(() => {
      const { certificateInfo } = props.workspace

      if (certificateInfo && certificateInfo?.status) {
        return certificateInfo
      }

      return false
    })

    const isRunning = computed(() => {
      return WorkspaceStates.isLoading(props.workspace.status)
    })

    const showPendingServicesPendingConfiguration = computed(() => {
      const space = spaceModule.watching

      if (space) {
        return space.schema.services.some(service => {
          const configuration = computed(() => service.userConfigurations as WizardConfigItem[])
          const { validate } = useWizard(configuration)

          return configuration.value.some(c => {
            return !validate(c.resourceKey)
          })
        })
      }

      return false
    })

    const showPendingUpdate = computed(() => {
      const { workspace } = props
      const workspaceDeployed = workspace as WorkspaceTemplate
      const updatedSchema = JSON.stringify({ assets: workspace.schema.assets, tags: workspace.schema.tags })
      const deployed = Utils.isType<WorkspaceTemplate>(workspaceDeployed, "schemaDeployed")

      if (workspace.status === Draft || isRunning.value) {
        return false
      } else if (deployed && deployed.schemaDeployed) {
        const deployedSchema = JSON.stringify({
          assets: deployed.schemaDeployed.assets,
          tags: deployed.schemaDeployed.tags
        })

        return updatedSchema !== deployedSchema && !showErrorAlert.value && usersModule.hasEditorPermissions
      }

      return false
    })

    const showNoAppsAlert = computed(() => {
      const workspaceSchema = props.workspace.schema.assets

      return workspaceSchema.length === 0
    })

    return {
      t,
      showPendingServicesPendingConfiguration,
      updateWarning,
      errorTypeDeployedWithError,
      disableErrorAlertActions,
      showDestroyFailedAlert,
      showNoAppsAlert,
      showPendingUpdate,
      showErrorAlert,
      showCertificate,
      certificate,
      isRunning
    }
  },
  components: {
    DeployedWithErrorAlert,
    NoApps,
    GenericAlert,
    ErrorAlert,
    DnsMessages,
    UpdateAlert
  }
})
</script>
